import type { IResponseError } from "./error";
import type { deputy_v1_messaging_Message } from "@deputy-ai/data-platform-api/v1/messaging";
import { Observable } from "rxjs";

export interface SSEMessageDelta {
  type: "delta";
  data: AgentOutput;
}

interface AgentOutput {
  type: "datastore_response_delta" | "graph_agent_output" | "agent_message";
  data: AgentToolFinished | AgentToolStarted | AgentMessageChunk | AgentMessage;
}

interface AgentToolFinished {
  type: "agent_tool_finished";
  name: string;
  tool_output: string;
}

interface AgentToolStarted {
  type: "agent_tool_started";
  name: string;
  tool_input: string;
}

interface AgentMessageChunk {
  type: "agent_message_chunk";
  text: string;
}

interface AgentMessage {
  type: "agent_message";
  message: deputy_v1_messaging_Message;
}

const dataStreamPrefix = "data: ";
const dataSeparator = "\n";

export class EventStreamReader<T> {
  private readonly _decoder = new TextDecoder();
  private _buffer = "";
  private _done = false;

  constructor(
    private readonly _reader: ReadableStreamDefaultReader<Uint8Array>
  ) {}

  async abort() {
    await this._reader.cancel();
  }

  async nextjson(): Promise<T | undefined> {
    while (true) {
      const line = await this.nextline();
      if (!line) {
        return undefined;
      }
      if (!line.startsWith(dataStreamPrefix)) {
        continue;
      }
      const jsonString = line.substring(
        line.indexOf(dataStreamPrefix) + dataStreamPrefix.length
      );
      return JSON.parse(jsonString);
    }
  }

  async nextline() {
    while (true) {
      const line = this._nextlineFromBuffer();
      if (line) {
        return line;
      }
      if (this._done && this._buffer.trim().length === 0) {
        return undefined;
      }
      const data = await this._reader.read();
      if (data.value) {
        const text = this._decoder.decode(data.value);
        this._buffer += text;
      }
      if (data.done) {
        this._done = true;
      }
    }
  }

  private _nextlineFromBuffer() {
    const index = this._buffer.indexOf(dataSeparator);
    if (index < 0) {
      return undefined;
    }
    const line = this._buffer.substring(0, index);
    this._buffer = this._buffer.substring(index + dataSeparator.length);
    return line.trim();
  }
}

export class ServerSentStreamResponse<ResponseT> {
  private _isAborted = false;

  constructor(
    private readonly _reader: EventStreamReader<ResponseT>,
    private readonly _abortController: AbortController
  ) {}

  async abort() {
    this._isAborted = true;
    await this._reader.abort();
    this._abortController.abort();
  }

  async next(): Promise<ResponseT | undefined> {
    if (this._isAborted) {
      throw {
        isCancel: true,
        detail: "abort reading",
        status: 499,
        responseError: true,
      } satisfies IResponseError;
    }
    const json = await this._reader.nextjson();
    if (!json) {
      return undefined;
    }
    return json;
  }
}

export interface SendSSERequestProps<RequestT> {
  url: string;
  request: RequestT;
}

export async function sendSSERequest<RequestT, ResponseT>({
  url,
  request,
}: SendSSERequestProps<RequestT>): Promise<
  ServerSentStreamResponse<ResponseT>
> {
  const body = JSON.stringify(request);
  const headers: Record<string, string> = {};
  const abortControlelr = new AbortController();
  headers["X-Avoid-GZip"] = "true";
  const resp = await fetch(url, {
    method: "POST",
    body,
    headers,
    signal: abortControlelr.signal,
  });
  if (!resp.ok || !resp.body) {
    throw {
      isCancel: resp.status === 499,
      detail: resp.body ? await resp.text() : "",
      responseError: true,
      status: resp.status,
    } satisfies IResponseError;
  }
  const reader = resp.body.getReader();
  const eventReader = new EventStreamReader<ResponseT>(reader);
  return new ServerSentStreamResponse(eventReader, abortControlelr);
}

export interface SendSSEGetRequestProps {
  url: string;
}

export async function sendSSEGetRequest<ResponseT>({
  url,
}: SendSSEGetRequestProps): Promise<ServerSentStreamResponse<ResponseT>> {
  const headers: Record<string, string> = {};
  const abortControlelr = new AbortController();
  headers["X-Avoid-GZip"] = "true";
  const resp = await fetch(url, {
    method: "GET",
    headers,
    signal: abortControlelr.signal,
  });
  if (!resp.ok || !resp.body) {
    throw {
      isCancel: resp.status === 499,
      detail: resp.body ? await resp.text() : "",
      responseError: true,
      status: resp.status,
    } satisfies IResponseError;
  }
  const reader = resp.body.getReader();
  const eventReader = new EventStreamReader<ResponseT>(reader);
  return new ServerSentStreamResponse(eventReader, abortControlelr);
}

export function toObservable<ResponseT>(
  resp: ServerSentStreamResponse<ResponseT>
) {
  return new Observable<ResponseT>((subscriber) => {
    let isComplete = false;
    async function run() {
      try {
        while (true) {
          const data = await resp.next();
          if (!data) {
            break;
          }
          subscriber.next(data);
        }
      } catch {
        subscriber.error();
      } finally {
        isComplete = true;
        subscriber.complete();
      }
    }

    void run();

    return () => {
      if (!isComplete) {
        void resp.abort();
      }
    };
  });
}
